//
// Copyright (c) 2025 Comcast
//

import { useState } from 'react'
import PropTypes from 'prop-types'
import styled from 'styled-components'

import colors from 'components/utils/colors'

import TableBody from './TableBody'

const TableBodyExpandedRow = styled(TableBody)`
  border-bottom: 1px solid ${colors.borderSecondaryDefault};
`

const StyledTableBodyWithChildren = styled(TableBody)`
  border-bottom: 1px solid ${colors.borderSecondaryDefault};
`

// Abstract renderer of expandable table rows.
// It gets 'items' array of any elements (they should have id property)
// and renders required TableBody's structure and executes external renderers
// onRenderRow - for 1 table row (item is passed)
// onRenderExpandedRows - for child rows of the expanded row (parent item is passed)
const ExpandableTableRows = ({
  items, onRenderRow, onRenderExpandedRows, onRowExpandToggle, bodyProps,
}) => {
  const [expandedRowIds, setExpandedRowIds] = useState([])

  const onRowExpandToggle_ = (id, isExpanded) => {
    const newExpandedRowIds = isExpanded
      ? expandedRowIds.filter(rowId => rowId !== id)
      : [...expandedRowIds, id]

    setExpandedRowIds(newExpandedRowIds)
    onRowExpandToggle?.({
      id: id,
      isExpanded: !isExpanded,
      expandedRowIds: newExpandedRowIds,
    })
  }

  const renderTableBodies = () => {
    const bodies = []
    let currentBodyRows = []

    items.forEach((item, index) => {
      const isExpanded = expandedRowIds.includes(item.id)
      currentBodyRows.push(
        onRenderRow({
          item: item,
          isExpanded: isExpanded,
          onExpandToggle: () => onRowExpandToggle_(item.id, isExpanded),
        })
      )

      if (isExpanded) {
        bodies.push(
          <TableBodyExpandedRow
            key={`body-${index}-before`}
            {...bodyProps}
          >
            {currentBodyRows}
          </TableBodyExpandedRow>
        )
        currentBodyRows = []
        bodies.push(
          <StyledTableBodyWithChildren
            key={`body-${index}-expanded`}
            darkBg
            {...bodyProps}
          >
            {onRenderExpandedRows({ item })}
          </StyledTableBodyWithChildren>
        )
      }
    })

    if (currentBodyRows.length > 0) {
      bodies.push(<TableBody key='body-last' {...bodyProps}>{currentBodyRows}</TableBody>)
    }

    return bodies
  }

  return <>{renderTableBodies()}</>
}

ExpandableTableRows.propTypes = {
  items: PropTypes.array.isRequired,
  onRenderRow: PropTypes.func.isRequired,
  onRenderExpandedRows: PropTypes.func.isRequired,
  onRowExpandToggle: PropTypes.func,
  bodyProps: PropTypes.object,
}

export default ExpandableTableRows
